import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const empty = {
  image_url: "", mobile_image_url: "", link_url: "", title: "", subtitle: "",
  badge: "", button_text: "", button_link: "", text_alignment: "left",
  overlay_strength: 60, order: 0, is_active: true,
};

function BannerForm({ initial, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const p = {
        ...form,
        order: Number(form.order) || 0,
        overlay_strength: Math.min(Math.max(Number(form.overlay_strength) || 0, 0), 100),
      };
      if (initial?.id) await api.put(`/banners/${initial.id}`, p);
      else await api.post("/banners", p);
      toast.success("Banner tersimpan");
      onSaved?.();
    } catch (e) { notifyError(e); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div><Label>URL Gambar (Desktop)</Label><Input value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} required placeholder="https://..." data-testid="banner-form-image-url" /></div>
      <div><Label>URL Gambar Mobile (opsional)</Label><Input value={form.mobile_image_url || ""} onChange={(e) => upd("mobile_image_url", e.target.value)} placeholder="https://... (rasio potret/persegi)" data-testid="banner-form-mobile-image-url" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Judul</Label><Input value={form.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="banner-form-title" /></div>
        <div><Label>Sub Judul</Label><Input value={form.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} data-testid="banner-form-subtitle" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Badge (opsional)</Label><Input value={form.badge || ""} onChange={(e) => upd("badge", e.target.value)} placeholder="Promo" data-testid="banner-form-badge" /></div>
        <div><Label>Teks Tombol</Label><Input value={form.button_text || ""} onChange={(e) => upd("button_text", e.target.value)} placeholder="Belanja Sekarang" data-testid="banner-form-button-text" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Link Tombol</Label><Input value={form.button_link || ""} onChange={(e) => upd("button_link", e.target.value)} placeholder="/katalog" data-testid="banner-form-button-link" /></div>
        <div><Label>Link URL (fallback)</Label><Input value={form.link_url || ""} onChange={(e) => upd("link_url", e.target.value)} placeholder="/katalog" data-testid="banner-form-link-url" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Posisi Teks</Label>
          <select value={form.text_alignment || "left"} onChange={(e) => upd("text_alignment", e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="banner-form-text-alignment">
            <option value="left">Kiri</option>
            <option value="center">Tengah</option>
            <option value="right">Kanan</option>
          </select>
        </div>
        <div><Label>Overlay (0-100)</Label><Input type="number" min={0} max={100} value={form.overlay_strength ?? 60} onChange={(e) => upd("overlay_strength", e.target.value)} data-testid="banner-form-overlay" /></div>
        <div><Label>Urutan</Label><Input type="number" value={form.order} onChange={(e) => upd("order", e.target.value)} data-testid="banner-form-order" /></div>
      </div>
      <Label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => upd("is_active", v)} data-testid="banner-form-active-switch" /> Aktif</Label>
      <DialogFooter><Button type="submit" className="brand-btn-primary rounded-xl" data-testid="banner-form-submit">Simpan</Button></DialogFooter>
    </form>
  );
}

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/banners", { params: { active_only: false } }).then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Hapus banner?")) return;
    await api.delete(`/banners/${id}`);
    load();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black">Banner Promo</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl"><Plus size={14} /> Tambah Banner</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Banner</DialogTitle></DialogHeader>
            <BannerForm initial={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {banners.map((b) => (
          <div key={b.id} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden">
            <div className="aspect-[16/9] bg-[#FAF9F6]">
              {b.image_url && <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold">{b.title || "Tanpa Judul"}</div>
                  <div className="text-xs text-[#5C5C5C]">{b.subtitle}</div>
                </div>
                <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${b.is_active ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-red-100 text-red-700"}`}>{b.is_active ? "Aktif" : "Nonaktif"}</span>
              </div>
              <div className="text-xs text-[#5C5C5C] mt-1">Urutan: {b.order} · Link: {b.link_url || "-"}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setEditing(b); setOpen(true); }} className="rounded-lg"><Pencil size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(b.id)} className="rounded-lg text-[#DC2F02]"><Trash2 size={12} /></Button>
              </div>
            </div>
          </div>
        ))}
        {banners.length === 0 && <div className="md:col-span-2 text-center py-12 text-[#5C5C5C] bg-white rounded-2xl border border-[#E5E5E5]">Belum ada banner</div>}
      </div>
    </AdminLayout>
  );
}
